import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { trackEvent } from '../utils/analytics';
import {
  COLLECTION_EVENT_INQUIRIES,
  EventIntakeDoc,
} from '../constants/firestore';
import {
  ALCOHOL_POLICY,
  EventIntakeCategory,
  EventIntakeConfig,
  EventIntakeField,
} from '../constants/eventIntake';

interface EventIntakeModalProps {
  isOpen: boolean;
  config: EventIntakeConfig | null;
  onClose: () => void;
}

type FormValues = Record<string, string>;

const contactFieldNames = new Set([
  'company_name',
  'company_group_name',
  'client_name',
  'contact_person',
  'phone',
  'email',
]);

function getUtmParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source'),
    utm_medium: p.get('utm_medium'),
    utm_campaign: p.get('utm_campaign'),
    utm_content: p.get('utm_content'),
    utm_term: p.get('utm_term'),
    fbclid: p.get('fbclid'),
    gclid: p.get('gclid'),
  };
}

function getDeviceType(): string {
  const w = window.innerWidth;
  return w <= 768 ? 'mobile' : w <= 1024 ? 'tablet' : 'desktop';
}

function getInitialValues(config: EventIntakeConfig): FormValues {
  return config.sections.reduce<FormValues>((acc, section) => {
    section.fields.forEach((field) => {
      acc[field.name] = '';
    });
    return acc;
  }, {});
}

function getContactName(category: EventIntakeCategory, values: FormValues): string {
  if (category === 'private_party') return values.client_name || '';
  return values.contact_person || '';
}

function getOrganizationName(category: EventIntakeCategory, values: FormValues): string | null {
  if (category === 'corporate_event') return values.company_name || null;
  if (category === 'full_club_buyout') return values.company_group_name || null;
  return null;
}

const EventIntakeModal: React.FC<EventIntakeModalProps> = ({ isOpen, config, onClose }) => {
  const initialValues = useMemo(() => (config ? getInitialValues(config) : {}), [config]);
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formStartedRef = useRef(false);

  useEffect(() => {
    setValues(initialValues);
    setErrors({});
    setIsSuccess(false);
    formStartedRef.current = false;
  }, [initialValues]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (config) trackEvent('form_view', { form: 'events_intake', event_category: config.id });
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, config]);

  if (!isOpen || !config) return null;

  const set = (field: string, value: string) => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackEvent('form_start', { form: 'events_intake', event_category: config.id });
    }
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const value = values[field.name]?.trim() || '';
        if (field.required && !value) {
          nextErrors[field.name] = `${field.label} is required`;
        }
        if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          nextErrors[field.name] = 'Please enter a valid email';
        }
        if (field.type === 'tel' && value && !/^[\d\s()+-]{7,}$/.test(value)) {
          nextErrors[field.name] = 'Please enter a valid phone number';
        }
      });
    });
    if (Object.keys(nextErrors).length > 0) {
      nextErrors.submit = 'Please complete the required fields above.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const close = () => {
    if (!isSuccess && formStartedRef.current) {
      trackEvent('form_close', { form: 'events_intake', event_category: config.id, had_input: true });
    }
    setValues(initialValues);
    setErrors({});
    setIsSuccess(false);
    formStartedRef.current = false;
    onClose();
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const utm = getUtmParams();
      const intakeAnswers = Object.entries(values).reduce<Record<string, string | null>>((acc, [key, value]) => {
        if (!contactFieldNames.has(key)) {
          acc[key] = value.trim() || null;
        }
        return acc;
      }, {});

      const payload: EventIntakeDoc = {
        doc_type: 'events_intake_v1',
        event_category: config.id,
        contact_name: getContactName(config.id, values).trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim(),
        organization_name: getOrganizationName(config.id, values),
        intake_answers: intakeAnswers,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_content: utm.utm_content,
        utm_term: utm.utm_term,
        fbclid: utm.fbclid,
        gclid: utm.gclid,
        referrer: document.referrer || null,
        device_type: getDeviceType(),
        created_at: serverTimestamp(),
        source: 'events_page',
        status: 'new',
      };

      const ref = doc(collection(db, COLLECTION_EVENT_INQUIRIES));
      await setDoc(ref, payload);
      trackEvent('event_intake_submit', { event_category: config.id, ...utm });
      setIsSuccess(true);
    } catch (err) {
      console.error('Event intake error:', err);
      setErrors({ submit: 'Something went wrong. Please call us or try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="v2-modal-overlay" onClick={close} role="dialog" aria-modal="true">
        <div className="v2-modal-content events-intake-modal" onClick={(e) => e.stopPropagation()}>
          <button className="v2-modal-close" onClick={close} aria-label="Close" type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="v2-modal-success-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <h2>Thanks - we got your request.</h2>
          <p className="v2-modal-success-event">{config.cardTitle}</p>
          <p className="v2-modal-success-note">
            Our events team will review the details and follow up with next steps.
          </p>
          <button className="v2-btn v2-btn-block" onClick={close} type="button">
            Done
          </button>
        </div>
      </div>
    );
  }

  const renderField = (field: EventIntakeField) => {
    const id = `event-intake-${config.id}-${field.name}`;
    const value = values[field.name] || '';
    const error = errors[field.name];
    const commonProps = {
      id,
      name: field.name,
      value,
      disabled: isSubmitting,
      className: error ? 'err' : '',
      onChange: (evt: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        set(field.name, evt.target.value);
      },
    };

    if (field.type === 'textarea') {
      return <textarea {...commonProps} rows={3} placeholder={field.placeholder} />;
    }

    if (field.type === 'select') {
      return (
        <select {...commonProps}>
          <option value="">Select one</option>
          {field.options?.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'radio') {
      return (
        <div className="events-radio-group" role="radiogroup" aria-labelledby={`${id}-label`}>
          {field.options?.map((option) => (
            <label key={option} className="events-radio-option">
              <input
                type="radio"
                name={field.name}
                value={option}
                checked={value === option}
                disabled={isSubmitting}
                onChange={(evt) => set(field.name, evt.target.value)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }

    return <input {...commonProps} type={field.type} placeholder={field.placeholder} />;
  };

  return (
    <div className="v2-modal-overlay" onClick={close} role="dialog" aria-modal="true" aria-labelledby="event-intake-title">
      <div className="v2-modal-content events-intake-modal" onClick={(e) => e.stopPropagation()}>
        <button className="v2-modal-close" onClick={close} aria-label="Close" type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>

        <div className="v2-modal-summary">
          <span>Events</span>
          <span>{config.cardTitle}</span>
        </div>
        <h2 id="event-intake-title">{config.title}</h2>
        <p className="v2-modal-subtext">Share the details below and our team will follow up with availability and next steps.</p>

        <form onSubmit={handleSubmit} noValidate>
          {config.sections.map((section) => (
            <fieldset className="events-intake-section" key={section.title}>
              <legend>{section.title}</legend>
              <div className="v2-form-grid">
                {section.fields.map((field) => {
                  const isWide = field.type === 'textarea' || field.type === 'radio';
                  const id = `event-intake-${config.id}-${field.name}`;
                  return (
                    <div className={`v2-form-row${isWide ? ' full' : ''}`} key={field.name}>
                      <label id={`${id}-label`} htmlFor={field.type === 'radio' ? undefined : id}>
                        {field.label}{field.required ? ' *' : ''}
                      </label>
                      {renderField(field)}
                      {errors[field.name] && <span className="err">{errors[field.name]}</span>}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}

          <div className="events-policy-note">
            <strong>Alcohol Policy</strong>
            <span>{ALCOHOL_POLICY}</span>
          </div>

          {errors.submit && <div className="v2-form-error-banner">{errors.submit}</div>}

          <button type="submit" className="v2-btn v2-btn-block" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : config.submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventIntakeModal;
